import { ArpMediaDraft, deserializeSongSnapshot } from '@arpeggi/shared'
import cookies from 'browser-cookies'
import { observer } from 'mobx-react-lite'
import React, { FC, useEffect, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useDebounce } from 'use-debounce'
import { useAuth } from '../../../hooks/useAuth'
import { useDrafts } from '../../../queries/drafts'
import { useTreeState } from '../../../stores/Root'
import CenteredLoadingAnimation from '../../core/CenteredLoadingAnimation'
import { Column, Row, StyledScrollable, Text, VerticalCenteredRow } from '../../core/Components'
import { gray1, gray4 } from '../../platform/color'
import StudioCloseDraftComponent from '../../studio/activitybar/StudioCloseDraftComponent'
import Search from '../controls/Search'

interface DraftItemProps {
  draft: ArpMediaDraft
  onClick?: () => void
}
const DraftItem: FC<DraftItemProps> = observer(({ draft, onClick }) => {
  const [tempo, setTempo] = useState(null)
  const [hover, setHover] = useState(false)

  useEffect(() => {
    const song = deserializeSongSnapshot(draft.arp)
    setTempo(song.tempo)
  }, [draft])

  const timestamp = new Date(draft.updatedAt)

  return (
    <Row
      title={draft.name}
      css={{
        marginBottom: '15px',
        cursor: 'pointer',
        overflow: 'hidden'
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
      className="unselectable"
    >
      <Column>
        <VerticalCenteredRow
          className="fs-14 font-dm-sans fw-medium"
          css={{ color: gray1, marginBottom: '10px' }}
        >
          <img
            src="/static/icons/library_music_white_24dp.svg"
            style={{ width: '15px', height: '15px' }}
          />
          <span
            style={{ marginLeft: '15px', textDecoration: hover && 'underline' }}
            className="ellipsis-overflow"
          >
            {draft.name}
          </span>
        </VerticalCenteredRow>
        <VerticalCenteredRow css={{ marginLeft: '30px' }} className="fs-12 fw-bold font-dm-sans">
          <span style={{ color: `${gray4} !important` }}>
            {`${tempo} BPM | ${timestamp.getMonth() + 1}/${timestamp.getDate()}/${
              timestamp.getFullYear() % 100
            }`}
          </span>
        </VerticalCenteredRow>
      </Column>
    </Row>
  )
})

interface DraftsListProps {
  query?: string
}
const DraftsList: FC<DraftsListProps> = observer(({ query }) => {
  const { studio } = useTreeState()
  const { user } = useAuth()

  const { data, isLoading, hasNextPage, fetchNextPage } = useDrafts({
    // all: user.type === 'normal' ? false : true,
    order: 'desc',
    orderBy: 'updatedAt',
    arpFormat: 2,
    search: query
  })

  if (isLoading) {
    return <CenteredLoadingAnimation light />
  }

  return (
    <InfiniteScroll
      loader={<CenteredLoadingAnimation />}
      dataLength={data?.pages.length * 50}
      next={fetchNextPage}
      hasMore={hasNextPage}
      style={{ overflow: 'visible' }}
      scrollableTarget="scrollableDiv"
    >
      {data.pages.map((page) =>
        page.data.map((draft) => {
          const key = `${draft.id}_${draft.user.walletAddress}`

          if (studio.songChangedSinceSave) {
            return (
              <StudioCloseDraftComponent
                trigger={<DraftItem draft={draft} key={key} />}
                next={() => studio.loadSongFromDraft(draft)}
                key={`${key}_modal`}
              />
            )
          }

          return (
            <DraftItem draft={draft} key={key} onClick={() => studio.loadSongFromDraft(draft)} />
          )
        })
      )}
    </InfiniteScroll>
  )
})

const DraftSelector: FC = () => {
  const [query, setQuery] = React.useState('')
  const [debouncedQuery] = useDebounce(query, 750)

  // temporary workaround for fixing transaction
  const { getOrSetUserAuthToken } = useAuth()
  useEffect(() => {
    getOrSetUserAuthToken()
  }, [])

  return (
    <div>
      <p className="fs-16 fw-medium">My Drafts</p>
      <Search
        query={query}
        placeholder="Search your drafts"
        setQuery={setQuery}
        style={{ marginTop: '0', marginBottom: '10px' }}
      />
      <StyledScrollable
        css={{
          paddingLeft: '6px',
          paddingRight: '6px',
          paddingTop: '0.25 rem',
          height: '80vh',
          maxHeight: '80vh'
        }}
        id="scrollableDiv"
      >
        {cookies.get('session') ? (
          <DraftsList query={debouncedQuery.trim()} />
        ) : (
          <Text css={{ textAlign: 'center' }}>
            Sign the transaction in your wallet to see your drafts
          </Text>
        )}
      </StyledScrollable>
    </div>
  )
}

export default observer(DraftSelector)
